import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Send, Check, X } from 'lucide-react';
import { financeApi } from '../../api/finance';
import { genericDocumentApi } from '../../api/generic-documents';
import { ApiError } from '../../api/client';
import { StatusBadge } from '../../components/StatusBadge';
import { CommentsThread } from '../../components/CommentsThread';
import { DocumentsSection } from '../../components/DocumentsSection';
import { RecordHeader } from '../../components/RecordHeader';
import { WorkflowTrackingTab } from '../../components/WorkflowTrackingTab';
import { EXPENSE_CLAIM_STAGES } from '../../config/workflow-stages';
import { WorkspaceTabs } from '../../components/WorkspaceTabs';
import { useToast } from '../../components/Toast';
import { usePermission } from '../../auth/AuthContext';
import { formatDate } from '../../utils/formatDate';

const STATUS_LABELS: Record<string, string> = { draft: 'Draft', submitted: 'Submitted', approved: 'Approved', rejected: 'Rejected', paid: 'Paid' };

export function ExpenseClaimDetailPage() {
  const { id } = useParams<{ id: string }>();
  const claimId = Number(id);
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const canManage = usePermission('finance.expenses.approve');
  const [approving, setApproving] = useState(false);
  const [selectedFiscalYearId, setSelectedFiscalYearId] = useState<number | null>(null);

  const claimQuery = useQuery({ queryKey: ['expense-claim', claimId], queryFn: () => financeApi.getExpenseClaim(claimId) });
  const docsQuery = useQuery({ queryKey: ['expense-claim-documents', claimId], queryFn: () => genericDocumentApi.listFor('expense_claim', claimId) });
  const fiscalYearsQuery = useQuery({ queryKey: ['finance-fiscal-years'], queryFn: () => financeApi.listFiscalYears(), enabled: approving });
  const fiscalYearDetailQuery = useQuery({ queryKey: ['finance-fiscal-year-detail', selectedFiscalYearId], queryFn: () => financeApi.getFiscalYear(selectedFiscalYearId!), enabled: !!selectedFiscalYearId });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['expense-claim', claimId] });
  const submitMutation = useMutation({ mutationFn: () => financeApi.submitExpenseClaim(claimId), onSuccess: () => { showSuccess('Submitted.'); invalidate(); }, onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.') });
  const approveMutation = useMutation({
    mutationFn: (periodId: number) => financeApi.approveExpenseClaim(claimId, { fiscalYearId: selectedFiscalYearId!, accountingPeriodId: periodId }),
    onSuccess: (result) => { showSuccess(`Approved - journal ${result.data.journalReference}, payable ${result.data.payableReference}.`); invalidate(); setApproving(false); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to approve - check Finance Settings has the Accounts Payable account configured.'),
  });
  const rejectMutation = useMutation({ mutationFn: (comment: string) => financeApi.rejectExpenseClaim(claimId, comment), onSuccess: () => { showSuccess('Rejected.'); invalidate(); }, onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.') });

  const periods = fiscalYearDetailQuery.data?.periods ?? [];

  if (!claimQuery.data) return <div className="page"><div className="loading-state"><span className="loading-spinner" />Loading…</div></div>;
  const c = claimQuery.data.data;

  return (
    <div className="page">
      <Link to="/finance/expenses">&larr; Back to Expense Claims</Link>
      <RecordHeader
        title={`Expense Claim - ${c.claim_reference}`}
        entityType="expense_claim"
        entityId={claimId}
        fields={[
          { label: 'Expense date', value: formatDate(c.expense_date) },
          { label: 'Amount', value: Number(c.amount).toLocaleString() },
          { label: 'Account', value: c.account_code ? `${c.account_code} - ${c.account_name}` : '-' },
          { label: 'Status', value: <StatusBadge status={c.status} label={STATUS_LABELS[c.status]} /> },
        ]}
        actions={
          <span className="icon-action-group">
            {c.status === 'draft' && <button className="btn-primary" onClick={() => submitMutation.mutate()} disabled={submitMutation.isPending}><Send size={16} className="inline-icon" /> Submit</button>}
            {c.status === 'submitted' && <button className="btn-primary" onClick={() => setApproving((v) => !v)}><Check size={16} className="inline-icon" /> Approve</button>}
            {c.status === 'submitted' && <button className="button-danger" onClick={() => { const cm = window.prompt('Rejection reason:'); if (cm) rejectMutation.mutate(cm); }}><X size={16} className="inline-icon" /> Reject</button>}
          </span>
        }
        readOnly={!canManage}
      />

      {approving && (
        <section className="card">
          <h2>Approve Expense Claim</h2>
          <label>
            Fiscal Year
            <select onChange={(e) => setSelectedFiscalYearId(Number(e.target.value) || null)}>
              <option value="">- select -</option>
              {fiscalYearsQuery.data?.data.map((fy) => <option key={fy.id} value={fy.id}>{fy.year_label}</option>)}
            </select>
          </label>
          <label>
            Accounting Period
            <select onChange={(e) => { if (e.target.value) approveMutation.mutate(Number(e.target.value)); }}>
              <option value="">- select to approve -</option>
              {periods.map((p) => <option key={p.id} value={p.id}>{p.period_label}</option>)}
            </select>
          </label>
          <button type="button" className="link-button" onClick={() => setApproving(false)}>Cancel</button>
        </section>
      )}

      <WorkspaceTabs
        tabs={[
          {
            key: 'details',
            label: 'Details',
            content: (
              <>
                <section className="card">
                  <h3>Description</h3>
                  <p>{c.description}</p>
                </section>
                <CommentsThread entityType="expense_claim" entityId={claimId} />
              </>
            ),
          },
          {
            key: 'documents',
            label: 'Documents',
            content: (
              <DocumentsSection
                documents={docsQuery.data?.data ?? []}
                downloadUrl={(documentId) => genericDocumentApi.downloadUrl('expense_claim', documentId)}
                upload={(input) => genericDocumentApi.uploadFor('expense_claim', claimId, input)}
                onDelete={(documentId) => genericDocumentApi.deleteFor('expense_claim', documentId)}
                queryKeyToInvalidate={['expense-claim-documents', claimId]}
                hrCategories={false}
              />
            ),
          },
          {
            key: 'tracking',
            label: 'Tracking',
            content: <WorkflowTrackingTab entityType="expense_claim" entityId={claimId} actionStageMap={EXPENSE_CLAIM_STAGES} />,
          },
        ]}
      />
    </div>
  );
}
